import { useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import NewAssessment from './components/NewAssessment';
import AssessmentResults from './components/AssessmentResults';
import ReportView from './components/ReportView';
import ErrorBoundary from './components/ErrorBoundary';
import { type AssessmentResult } from './types/assessment';

export default function App() {
  const [assessmentResult, setAssessmentResult] = useState<AssessmentResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAssessmentComplete = useCallback((result: AssessmentResult) => {
    setAssessmentResult(result);
    setError(null);
  }, []);

  const handleError = useCallback((err: string) => {
    setError(err);
    setAssessmentResult(null);
  }, []);

  const handleClearSession = useCallback(() => {
    setAssessmentResult(null);
    setError(null);
  }, []);

  return (
    <ErrorBoundary>
      <div className="app">
        <header className="app-header">
          <h1>CrowdStrike Falcon Health Check Assessment</h1>
          <div className="header-meta">
            <span className="badge stateless">Stateless</span>
            <span className="badge secure">No Data Retention</span>
          </div>
        </header>

        {error && (
          <div className="error-banner">
            <strong>Error:</strong> {error}
            <button onClick={() => setError(null)} className="close-btn">&times;</button>
          </div>
        )}

        <main className="app-main">
          <Routes>
            <Route
              path="/"
              element={
                assessmentResult ? (
                  <Navigate to="/results" replace />
                ) : (
                  <NewAssessment
                    onComplete={handleAssessmentComplete}
                    onError={handleError}
                  />
                )
              }
            />
            <Route
              path="/results"
              element={
                assessmentResult ? (
                  <AssessmentResults
                    result={assessmentResult}
                    onClear={handleClearSession}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/report"
              element={
                assessmentResult ? (
                  <ReportView
                    result={assessmentResult}
                    onClear={handleClearSession}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </main>

        <footer className="app-footer">
          <p>
            <strong>Privacy Notice:</strong> This application does not store customer data,
            credentials, or assessment results. All information is held in browser memory only
            and is cleared on page refresh or when you click &quot;Clear Session&quot;.
          </p>
        </footer>
      </div>
    </ErrorBoundary>
  );
}
